import type { NativeCallSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt } from '../util/safe.js';

/**
 * Проверка встроенных методов на подмену — сравнением браузера с самим собой.
 *
 * Замысел. Если вызвать встроенный метод с негодным получателем, движок
 * отвечает ошибкой строго определённого вида, и вид этот у всех встроенных
 * методов одного движка один и тот же. В Chromium это «TypeError: Illegal
 * invocation», в других движках своя формулировка — но внутри одного движка
 * она едина. Метод, подменённый обычной функцией на JavaScript, ошибается
 * иначе: у него другое сообщение, другой вид ошибки, а часто он и вовсе не
 * бросает.
 *
 * Чем этот подход отличается от предыдущих. Все прежние проверки такого рода
 * сверяли значение с таблицей — «у WebKit должно быть вот так». Таблицы
 * писал я по памяти, и она трижды за сутки оказывалась неверной, обвиняя
 * настоящие браузеры. Здесь таблицы нет вовсе: эталон берётся из этого же
 * браузера, в эту же секунду. Ошибиться в нём я не могу по построению.
 *
 * Эталонами взяты методы, которые подделывать незачем: работа с деревом
 * документа не участвует ни в одном отпечатке. Если и они разойдутся между
 * собой — значит опереться не на что, и проверка честно молчит.
 *
 * Что это ловит. Подмену холста, геометрии, звука и графики в тот момент,
 * когда она поставлена, — независимо от того, включён ли шум прямо сейчас.
 * Проверяемые тождества ловят последствия подмены, эта проверка — саму
 * подмену.
 */

/** Методы, которые подделывать незачем: отпечатка в них нет. */
const REFERENCES: [string, () => void][] = [
  ['Node.appendChild', () => Node.prototype.appendChild.call(null as never, null as never)],
  ['Element.getAttribute', () => Element.prototype.getAttribute.call(null as never, 'x')],
  ['Node.cloneNode', () => Node.prototype.cloneNode.call(null as never)],
];

/** Методы, которые подделывают чаще всего. */
const SUSPECTS: [string, () => void][] = [
  ['toDataURL', () => HTMLCanvasElement.prototype.toDataURL.call(null as never)],
  ['toBlob', () => HTMLCanvasElement.prototype.toBlob.call(null as never, () => {})],
  ['getImageData', () => CanvasRenderingContext2D.prototype.getImageData.call(null as never, 0, 0, 1, 1)],
  ['getBoundingClientRect', () => Element.prototype.getBoundingClientRect.call(null as never)],
  ['getClientRects', () => Element.prototype.getClientRects.call(null as never)],
  ['AudioBuffer.getChannelData', () => AudioBuffer.prototype.getChannelData.call(null as never, 0)],
  ['WebGL.getParameter', () => WebGLRenderingContext.prototype.getParameter.call(null as never, 0)],
  ['WebGL.getExtension', () => WebGLRenderingContext.prototype.getExtension.call(null as never, 'x')],
];

/** Свойства, читаемые через геттер: их подменяют не реже методов. */
const GETTERS: [string, object, string][] = [
  ['navigator.userAgent', Navigator.prototype, 'userAgent'],
  ['navigator.platform', Navigator.prototype, 'platform'],
  ['navigator.hardwareConcurrency', Navigator.prototype, 'hardwareConcurrency'],
  ['screen.width', Screen.prototype, 'width'],
  ['screen.height', Screen.prototype, 'height'],
];

/**
 * Форма ответа на вызов с негодным получателем.
 *
 * Сообщение обрезается: у некоторых движков в него попадает имя самого
 * метода, и тогда формы законно различались бы.
 */
function shapeOf(call: () => void): string {
  try {
    call();
    return 'без-ошибки';
  } catch (error) {
    const e = error as { name?: string; message?: string };
    // Из сообщения берётся только его вид, без имён и значений: числа и
    // слова в кавычках убираются, чтобы сравнивались формулировки, а не
    // подробности конкретного вызова.
    const message = String(e?.message ?? '')
      .replace(/'[^']*'/g, "'…'")
      .replace(/"[^"]*"/g, '"…"')
      .replace(/\d+/g, 'N')
      .slice(0, 80);
    return `${e?.name ?? 'нет-имени'}|${message}`;
  }
}

export const nativeCallsModule = defineModule({
  id: 'nativecalls',
  phase: 2,
  timeoutMs: 2000,
  run(): NativeCallSignals {
    const empty: NativeCallSignals = {
      reference: null,
      mismatched: null,
      checked: null,
    };

    return (
      attempt(() => {
        // Эталон. Берётся из этого же браузера: если методы, которые никто
        // не трогает, отвечают одинаково — их общий ответ и есть норма.
        const referenceShapes = REFERENCES.map(([, call]) => shapeOf(call));
        const reference = referenceShapes[0];
        if (!reference || referenceShapes.some((s) => s !== reference)) {
          // Эталоны разошлись между собой — опереться не на что. Молчим:
          // отсутствие опоры не есть улика.
          return { ...empty, reference: null };
        }

        const mismatched: string[] = [];

        for (const [name, call] of SUSPECTS) {
          const shape = shapeOf(call);
          // Отсутствие метода — не подмена: в разных браузерах набор разный.
          if (shape.startsWith('TypeError|') && shape.includes('is not a function')) continue;
          if (shape !== reference) mismatched.push(`${name}: ${shape}`);
        }

        for (const [name, proto, prop] of GETTERS) {
          const descriptor = Object.getOwnPropertyDescriptor(proto, prop);
          const getter = descriptor?.get;
          if (typeof getter !== 'function') continue;
          const shape = shapeOf(() => getter.call(null));
          if (shape !== reference) mismatched.push(`${name}: ${shape}`);
        }

        return {
          reference,
          mismatched,
          checked: SUSPECTS.length + GETTERS.length,
        };
      }) ?? empty
    );
  },
});
